const hasLocalStorage = (): boolean => typeof localStorage !== 'undefined';

/**
 * Read a JSON-serialized value from `localStorage`. Returns `null` when the key is
 * absent, when storage is unavailable (SSR / privacy mode), or when the stored value
 * is not valid JSON. Callers should provide a default via `??`.
 *
 * @example
 *   const auths = storage.getItem<IAuthorize[]>('pitta_authorize') ?? [];
 */
const getItem = <T = unknown>(key: string): T | null => {
  if (!key) {
    throw new Error('storage.getItem: key must be a non-empty string');
  }
  if (!hasLocalStorage()) {
    return null;
  }
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) {
      return null;
    }
    return JSON.parse(raw) as T;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn(`storage.getItem('${key}') failed:`, e);
    return null;
  }
};

const saveItem = (key: string, value: unknown): void => {
  if (!hasLocalStorage()) {
    return;
  }
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn(`storage.saveItem('${key}') failed:`, e);
  }
};

const removeItem = (key: string): void => {
  if (!hasLocalStorage()) {
    return;
  }
  try {
    localStorage.removeItem(key);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn(`storage.removeItem('${key}') failed:`, e);
  }
};

const storage = {
  saveItem,
  getItem,
  removeItem,
};

export default storage;
